
import React, { useEffect, useLayoutEffect, useState, useCallback, useRef } from 'react';
import './SocialModal.css';
import './SocialModalInvite.css';
import BackgroundPositionHistory from './BackgroundPositionHistory';
import useDojoMembershipRead from '../hooks/useDojoMembershipRead';
import DojoJoinSettingsConsent, { joinSettingChanges, joinConsentOptions } from './DojoJoinSettingsConsent';
import DojoCoachMembershipNotice from './DojoCoachMembershipNotice';
import GracePeriodCountdown from './GracePeriodCountdown';
import { useModal } from '../services/ModalService';
import SocialProfileModal from './SocialProfileModal';
import DojoSettingsModal from './DojoSettingsModal';
import { getAvatarVariantUrl, avatarVariantForPixelSize } from '../utils/avatarVariants';

const activeJoinEligibility = eligibility => {
  if (!eligibility) return eligibility;
  const violations = (eligibility.violations || []).filter(v => v.type !== 'privacy' || v.activityType !== 'stats');
  const changes = Object.fromEntries(Object.entries(eligibility.suggestedPrivacyChanges || {})
    .filter(([category]) => category !== 'stats'));
  return { ...eligibility, violations, eligible: eligibility.eligible || violations.length === 0,
    suggestedPrivacyChanges: Object.keys(changes).length ? changes : null };
};

const SocialModal = ({ close, backendService, initialTab = 'messages', initialFriendId = null, initialDojoChat = false, onDojoChange, streams, dojoOnly = false }) => {
  const { open: openModal, updateProps } = useModal();
  const [activeTab, setActiveTab] = useState(dojoOnly ? 'dojo' : initialTab === 'leaderboard' || initialTab === 'dojo' ? 'friends' : initialTab);

  // Update modal props when tab changes so App.js can update title
  useEffect(() => {
    if (!dojoOnly) updateProps({ initialTab: activeTab });
  }, [activeTab, dojoOnly, updateProps]);

  const [friends, setFriends] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [sentRequests, setSentRequests] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingRequest, setIsSendingRequest] = useState(null); // Store the userId being sent, or null
  
  // Gym filter state (used in search tab)
  const [userGyms, setUserGyms] = useState([]);
  const [selectedGymFilter, setSelectedGymFilter] = useState('none');
  
  // Friend invite by email state
  const [friendInviteEmail, setFriendInviteEmail] = useState('');
  const [isSendingFriendInvite, setIsSendingFriendInvite] = useState(false);
  
  // Current user ID for "you" indicator
  const currentUser = backendService.getUser?.();
  const currentUserId = currentUser?._id || currentUser?.user_id || currentUser?.id || null;

  // Helper to check if a user ID matches current user
  const isCurrentUser = useCallback((userId) => {
    if (!currentUserId || !userId) return false;
    return String(userId) === String(currentUserId);
  }, [currentUserId]);

  // Helper to render name with "(you)" indicator if it's the current user
  const renderNameWithYouIndicator = useCallback((name, userId) => {
    if (isCurrentUser(userId)) {
      return <>{name} <span className="you-indicator">(you)</span></>;
    }
    return name;
  }, [isCurrentUser]);

  // Messaging state
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(initialFriendId);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const messagesEndRef = useRef(null);
  const messageInputRef = useRef(null);
  
  // Dojo state

  const [dojoMembers, setDojoMembers] = useState([]);
  const [dojoJoinRequests, setDojoJoinRequests] = useState([]);
  const [dojoInvites, setDojoInvites] = useState([]);
  const [sentDojoInvites, setSentDojoInvites] = useState([]);
  const [searchDojos, setSearchDojos] = useState([]);
  const [dojoSearchQuery, setDojoSearchQuery] = useState('');
  const [dojoView, setDojoView] = useState('my-dojo'); // 'my-dojo', 'find', 'create', 'chat'

  const [showInvitePanel, setShowInvitePanel] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [inviteLinkError, setInviteLinkError] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteCopied, setInviteCopied] = useState(false);
  
  // Dojo chat state
  const [dojoMessages, setDojoMessages] = useState([]);
  const [newDojoMessage, setNewDojoMessage] = useState('');
  const [isSendingDojoMessage, setIsSendingDojoMessage] = useState(false);
  const [showDojoChat, setShowDojoChat] = useState(dojoOnly && initialDojoChat);
  const dojoMessagesEndRef = useRef(null);
  
  // Dojo requirements state (just for display, actual editing in modal)
  const [dojoRequirements, setDojoRequirements] = useState(null);
  const [myCompliance, setMyCompliance] = useState(null);
  
  // Create dojo form
  const [newDojoName, setNewDojoName] = useState('');
  const [newDojoDescription, setNewDojoDescription] = useState('');
  const [newDojoVisibility, setNewDojoVisibility] = useState('public');
  const [newDojoEmoji, setNewDojoEmoji] = useState('🥋');
  const [newDojoTone, setNewDojoTone] = useState('drill_sergeant');
  const [newDojoIntensity, setNewDojoIntensity] = useState('intense');
  const [newDojoToneCustom, setNewDojoToneCustom] = useState('');
  const [newDojoIntensityCustom, setNewDojoIntensityCustom] = useState('');


  
  // Join dojo confirmation state
  const [joinConfirmDojo, setJoinConfirmDojo] = useState(null);
  const [joinEligibility, setJoinEligibility] = useState(null);
  const [joinAcceptRequiredSettings, setJoinAcceptRequiredSettings] = useState(false);
  const [joinAcceptBackground, setJoinAcceptBackground] = useState(false);
  const [trailMember, setTrailMember] = useState(null);
  const { dojo: myDojo, status: membershipStatus, load: readMembership, captureEmpty, captureAccount } = useDojoMembershipRead(backendService);
  const [joinAcceptCoachSettings, setJoinAcceptCoachSettings] = useState(false);
  useLayoutEffect(() => { setTrailMember(null); setJoinConfirmDojo(null); setJoinEligibility(null); setJoinAcceptRequiredSettings(false); setJoinAcceptBackground(false); setJoinAcceptCoachSettings(false); }, [captureAccount]);
  const inviteRead = useRef(0), searchRead = useRef(0);
  useLayoutEffect(() => {
    inviteRead.current++; searchRead.current++;
    setDojoInvites([]); setSentDojoInvites([]); setDojoView('my-dojo'); setJoinConfirmDojo(null);
    setSearchDojos([]); setIsLoading(false);
  }, [captureAccount]);

  const tonePresets = [
    { value: 'drill_sergeant', label: '🎖️ Drill Sergeant' },
    { value: 'supportive_friend', label: '🤗 Supportive Friend' },
    { value: 'zen_master', label: '🧘 Zen Master' },
    { value: 'sports_coach', label: '🏆 Sports Coach' },
    { value: 'scientist', label: '🔬 Scientist' },
    { value: 'custom', label: '✏️ Custom' }
  ];
  const intensityLevels = [
    { value: 'gentle', label: 'Gentle' },
    { value: 'moderate', label: 'Moderate' },
    { value: 'intense', label: 'Intense' },
    { value: 'extreme', label: 'Extreme' },
    { value: 'custom', label: 'Custom' }
  ];


  const loadFriends = useCallback(async () => {
    try {
      const data = await backendService.getFriends();
      setFriends(data);
    } catch (error) {
      console.error('Error loading friends:', error);
    }
  }, [backendService]);

  const loadRequests = useCallback(async () => {
    try {
      const [pending, sent] = await Promise.all([
        backendService.getPendingFriendRequests(),
        backendService.getSentFriendRequests()
      ]);
      setPendingRequests(pending);
      setSentRequests(sent);
    } catch (error) {
      console.error('Error loading requests:', error);
    }
  }, [backendService]);

  const loadConversations = useCallback(async () => {
    try {
      const data = await backendService.getConversations();
      setConversations(data);
    } catch (error) {
      console.error('Error loading conversations:', error);
    }
  }, [backendService]);

  // Dojo loading functions
  const loadMyDojo = useCallback(async () => {
    if (!captureAccount().current()) return;
    setDojoMembers([]); setDojoJoinRequests([]); setDojoRequirements(null); setMyCompliance(null);
    const read = await readMembership();
    if (!read?.dojo || !read.current()) return;
    const { dojo, current } = read;
    try {
      const members = await backendService.getDojoMembers(dojo._id);
      if (!current()) return;
      setDojoMembers(members);
      if (dojo.user_membership?.role !== 'member') {
        const requests = await backendService.getDojoJoinRequests(dojo._id);
        if (!current()) return;
        setDojoJoinRequests(requests);
      }
    } catch (error) {
      // A roster failure cannot turn established membership into onboarding.
      if (current()) console.error('Error loading dojo roster:', error);
    }
    if (!current()) return;
    try {
      const data = await backendService.getDojoRequirements(dojo._id);
      if (!current()) return;
      setDojoRequirements(data?.requirements || null);
      setMyCompliance(data?.compliance && !data.compliance.compliant ? data.compliance : null);
    } catch (error) {
      if (current()) console.error('Error loading dojo requirements:', error);
    }
  }, [backendService, readMembership, captureAccount]);

  const loadDojoInvites = useCallback(async () => {
    const account = captureAccount(), request = ++inviteRead.current;
    const current = () => account.current() && request === inviteRead.current;
    try {
      const [invites, sent] = await Promise.all([backendService.getDojoInvites(), backendService.getSentDojoInvites()]);
      if (!current()) return;
      setDojoInvites(invites); setSentDojoInvites(sent);
    } catch (error) {
      if (current()) console.error('Error loading dojo invites:', error);
    }
  }, [backendService, captureAccount]);

  // Gym filter - load user's gyms for the search filter
  const loadGyms = useCallback(async () => {
    try {
      const gyms = await backendService.getGyms();
      setUserGyms(gyms);
    } catch (error) {
      console.error('Error loading gyms:', error);
    }
  }, [backendService]);

  const handleDojoSearch = useCallback(async () => {
    const account = captureAccount(), request = ++searchRead.current;
    const current = () => account.current() && request === searchRead.current;
    if (!account.current()) return;
    try {
      setIsLoading(true);
      const results = await backendService.searchDojos(dojoSearchQuery);
      if (current()) setSearchDojos(results);
    } catch (error) {
      if (current()) console.error('Error searching dojos:', error);
    } finally {
      if (current()) setIsLoading(false);
    }
  }, [backendService, dojoSearchQuery, captureAccount]);

  const loadMessages = useCallback(async (friendId) => {
    if (!friendId) return;
    try {
      const data = await backendService.getConversation(friendId);
      setMessages(data);
      // Mark as read
      await backendService.markMessagesAsRead(friendId);
      // Refresh conversations to update unread counts
      loadConversations();
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  }, [backendService, loadConversations]);

  useEffect(() => {
    loadFriends();
    loadRequests();
    loadConversations();
    loadDojoInvites();
    loadGyms();
  }, [loadFriends, loadRequests, loadConversations, loadDojoInvites, loadGyms]);

  useEffect(() => { loadMyDojo(); }, [loadMyDojo]);

  useEffect(() => {
    if (activeTab === 'dojo' && dojoView === 'find') {
      handleDojoSearch();
    }
  }, [activeTab, dojoView, handleDojoSearch]);

  const loadDojoMessages = useCallback(async () => {
    if (!myDojo?._id) return;
    try {
      const msgs = await backendService.getDojoMessages(myDojo._id);
      setDojoMessages(msgs);
    } catch (error) {
      console.error('Error loading dojo messages:', error);
    }
  }, [backendService, myDojo?._id]);

  useEffect(() => {
    if (myDojo?._id) {
      loadDojoMessages();
    }
  }, [myDojo?._id, loadDojoMessages]);

  useEffect(() => {
    if (selectedConversation) {
      loadMessages(selectedConversation);
    }
  }, [selectedConversation, loadMessages]);

  useEffect(() => {
    // Scroll to bottom when messages change
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    // Scroll to bottom when dojo messages change
    dojoMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [dojoMessages]);

  // WebSocket listener for new messages
  useEffect(() => {
    const handleWebSocketMessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'new_message') {
          // If we're in the conversation with this sender, add the message
          if (data.message.from_user_id === selectedConversation) {
            setMessages(prev => [...prev, data.message]);
            backendService.markMessagesAsRead(selectedConversation);
          }
          // Refresh conversations list
          loadConversations();
        } else if (data.type === 'new_dojo_message') {
          // If this message is for our dojo, add it
          const msgDojoId = data.message.dojo_id?.toString() || data.dojoId;
          if (msgDojoId === myDojo?._id?.toString()) {
            setDojoMessages(prev => {
              // Avoid duplicates
              if (prev.some(m => m._id?.toString() === data.message._id?.toString())) return prev;
              return [...prev, data.message];
            });
          }
        }
      } catch (e) {
        // Not JSON or not relevant
      }
    };

    // This assumes there's a global WebSocket connection
    // You may need to adapt this to your WebSocket setup
    if (window.appWebSocket) {
      window.appWebSocket.addEventListener('message', handleWebSocketMessage);
      return () => {
        if (window.appWebSocket) {
          window.appWebSocket.removeEventListener('message', handleWebSocketMessage);
        }
      };
    }
  }, [selectedConversation, backendService, loadConversations, myDojo?._id]);

  const handleSearch = useCallback(async () => {
    // If a gym is selected, search by gym (no text threshold needed)
    // If no gym selected, require at least 2 characters for text search
    const hasGymFilter = selectedGymFilter && selectedGymFilter !== 'none';
    const hasTextQuery = searchQuery.length >= 2;
    
    if (!hasGymFilter && !hasTextQuery) {
      setSearchResults([]);
      return;
    }
    
    setIsLoading(true);
    try {
      let results;
      if (hasGymFilter) {
        // Search by gym, optionally filtered by text
        const gymId = selectedGymFilter === 'all' ? null : selectedGymFilter;
        results = await backendService.searchUsersByGym(gymId);
        // If there's also a text query, filter the results
        if (hasTextQuery) {
          const query = searchQuery.toLowerCase();
          results = results.filter(u => 
            (u.display_name?.toLowerCase().includes(query)) ||
            (u.email?.toLowerCase().includes(query))
          );
        }
      } else {
        // Text-only search
        results = await backendService.searchUsers(searchQuery);
      }
      setSearchResults(results);
    } catch (error) {
      console.error('Error searching users:', error);
    } finally {
      setIsLoading(false);
    }
  }, [backendService, searchQuery, selectedGymFilter]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      if (activeTab === 'search') {
        handleSearch();
      }
    }, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery, selectedGymFilter, activeTab, handleSearch]);

  const handleSendRequest = async (userId) => {
    if (isSendingRequest) return;
    setIsSendingRequest(userId); // Track which user we're sending to
    try {
      await backendService.sendFriendRequest(userId);
      await handleSearch();
      await loadRequests();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to send friend request');
    } finally {
      setIsSendingRequest(null);
    }
  };

  const handleAcceptRequest = async (requestId) => {
    try {
      await backendService.acceptFriendRequest(requestId);
      loadFriends();
      loadRequests();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to accept friend request');
    }
  };

  const handleDeclineRequest = async (requestId) => {
    try {
      await backendService.declineFriendRequest(requestId);
      loadRequests();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to decline friend request');
    }
  };

  const handleCancelRequest = async (requestId) => {
    try {
      await backendService.cancelFriendRequest(requestId);
      loadRequests();
      handleSearch();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to cancel friend request');
    }
  };

  const handleRemoveFriend = async (friendId) => {
    if (!window.confirm('Are you sure you want to remove this friend?')) return;
    try {
      await backendService.removeFriend(friendId);
      loadFriends();
      loadConversations();
      if (selectedConversation === friendId) {
        setSelectedConversation(null);
        setMessages([]);
      }
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to remove friend');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation || isSendingMessage) return;
    
    setIsSendingMessage(true);
    try {
      const message = await backendService.sendMessage(selectedConversation, newMessage.trim());
      setMessages(prev => [...prev, message]);
      setNewMessage('');
      loadConversations();
      messageInputRef.current?.focus();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to send message');
    } finally {
      setIsSendingMessage(false);
    }
  };

  const handleStartConversation = (friendId) => {
    if (dojoOnly) {
      openModal(SocialModal, { backendService, initialTab: 'messages', initialFriendId: friendId, onDojoChange, streams });
      return;
    }
    setShowDojoChat(false);
    setSelectedConversation(friendId);
    setActiveTab('messages');
  };

  // Dojo action handlers
  const handleCreateDojo = async (e) => {
    e.preventDefault();
    const membership = captureEmpty(), account = captureAccount();
    if (!membership.current() || !newDojoName.trim()) return;
    try {
      setIsLoading(true);
      await backendService.createDojo({
        name: newDojoName,
        description: newDojoDescription,
        visibility: newDojoVisibility,
        ai_coach_settings: {
          emoji: newDojoEmoji,
          tone_preset: newDojoTone,
          tone_custom: newDojoTone === 'custom' ? newDojoToneCustom : '',
          intensity: newDojoIntensity,
          intensity_custom: newDojoIntensity === 'custom' ? newDojoIntensityCustom : ''
        }
      });
      if (!account.current()) return;
      setDojoView('my-dojo');
      setNewDojoName('');
      setNewDojoDescription('');
      await loadMyDojo();
      if (account.current()) onDojoChange?.();
    } catch (error) {
      if (!account.current()) return;
      alert(error.response?.data?.error || 'Failed to create dojo');
    } finally {
      if (account.current()) setIsLoading(false);
    }
  };

  const openJoinConfirmation = async (dojo, visibility = 'public') => {
    const account = captureEmpty();
    if (!account.current()) return;
    const eligibility = activeJoinEligibility(await backendService.checkDojoEligibility(dojo._id));
    if (!account.current()) return;
    setJoinConfirmDojo({ ...dojo, visibility, account });
    setJoinEligibility(eligibility);
    setJoinAcceptRequiredSettings(false); setJoinAcceptBackground(false);
    setJoinAcceptCoachSettings(!Object.keys(eligibility?.suggestedCoachAutoAcceptance || {}).length);
  };

  const handleJoinDojo = async (dojo, visibility) => {
    const completion = captureAccount();
    if (!captureEmpty().current()) return;
    try {
      setIsLoading(true);
      await openJoinConfirmation(dojo, visibility);
    } catch (error) {
      if (!completion.current()) return;
      alert(error.response?.data?.error || 'Failed to check dojo requirements');
    } finally {
      if (completion.current()) setIsLoading(false);
    }
  };

  const handleConfirmJoin = async () => {
    if (!captureEmpty().current() || !joinConfirmDojo || !joinEligibility || isLoading ||
        (joinConfirmDojo.visibility !== 'invite_only' && (
          (joinSettingChanges(joinEligibility).needsConsent && !joinAcceptRequiredSettings) ||
          (joinSettingChanges(joinEligibility).needsBackgroundConsent && !joinAcceptBackground) ||
          joinSettingChanges(joinEligibility).blocking ||
          (Object.keys(joinEligibility.suggestedCoachAutoAcceptance || {}).length && !joinAcceptCoachSettings)))) return;

    const account = joinConfirmDojo.account;
    const completion = captureAccount();
    if (!account?.current()) { setJoinConfirmDojo(null); return; }
    try {
      setIsLoading(true);
      // Consent applies to the displayed settings, not to later edits by a chief.
      const latest = activeJoinEligibility(await backendService.checkDojoEligibility(joinConfirmDojo._id));
      if (!account.current()) return;
      if (JSON.stringify({ requirementsFingerprint: latest?.requirementsFingerprint, requirements: latest?.requirements, violations: latest?.violations,
        currentStats: latest?.currentStats, suggestedCoachAutoAcceptance: latest?.suggestedCoachAutoAcceptance }) !==
          JSON.stringify({ requirementsFingerprint: joinEligibility.requirementsFingerprint, requirements: joinEligibility.requirements, violations: joinEligibility.violations,
            currentStats: joinEligibility.currentStats, suggestedCoachAutoAcceptance: joinEligibility.suggestedCoachAutoAcceptance })) {
        setJoinEligibility(latest);
        setJoinAcceptRequiredSettings(false); setJoinAcceptBackground(false);
        setJoinAcceptCoachSettings(!Object.keys(latest?.suggestedCoachAutoAcceptance || {}).length);
        return;
      }
      const requiredCoachSettings = latest.suggestedCoachAutoAcceptance || {};
      const options = {
        ...(joinConfirmDojo.visibility === 'invite_only' ? {} : joinConsentOptions(latest, joinAcceptRequiredSettings, joinAcceptBackground)),
        ...(Object.keys(requiredCoachSettings).length ? {
          acceptCoachAutoAcceptance: joinAcceptCoachSettings,
          coachAutoAcceptanceChanges: requiredCoachSettings
        } : {})
      };
      if (joinConfirmDojo.visibility === 'invite_only') {
        // A request is not membership. Recheck requirements and ask for fresh
        // consent when the approved invite is actually accepted.
        await backendService.requestToJoinDojo(joinConfirmDojo._id, 'I would like to join your dojo!');
        if (!completion.current()) return;
        alert('Join request sent!');
      } else {
        await backendService.joinDojo(joinConfirmDojo._id, options);
        if (!completion.current()) return;
        await loadMyDojo();
        if (!completion.current()) return;
        onDojoChange?.();
      }
      setJoinConfirmDojo(null);
      setJoinEligibility(null);
      setJoinAcceptRequiredSettings(false); setJoinAcceptBackground(false);
      await handleDojoSearch();
    } catch (error) {
      if (!completion.current()) return;
      if (error.response?.status === 409) {
        setJoinAcceptRequiredSettings(false); setJoinAcceptBackground(false);
        try {
          const fresh = activeJoinEligibility(await backendService.checkDojoEligibility(joinConfirmDojo._id));
          if (!account.current()) return;
          setJoinEligibility(fresh);
          setJoinAcceptCoachSettings(!Object.keys(fresh?.suggestedCoachAutoAcceptance || {}).length);
        } catch (refreshError) {
          setJoinEligibility(null); // Never reuse a stale disclosure after a conflict.
        }
      }
      alert(error.response?.data?.error || 'Failed to join dojo');
    } finally {
      if (completion.current()) setIsLoading(false);
    }
  };

  const handleCancelJoin = () => {
    setJoinConfirmDojo(null);
    setJoinEligibility(null);
    setJoinAcceptRequiredSettings(false); setJoinAcceptBackground(false);
  };

  const handleLeaveDojo = async () => {
    if (!window.confirm('Leave this dojo? Dojo-required Position sharing and GPS capture may remain enabled. Review Profile → Privacy after leaving to turn them off if you no longer want them.')) return;
    try {
      setIsLoading(true);
      await backendService.leaveDojo();
      await loadMyDojo();
      setDojoMembers([]);
      onDojoChange?.();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to leave dojo');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKickMember = async (userId) => {
    if (!window.confirm('Remove this member from the dojo?')) return;
    try {
      await backendService.kickDojoMember(myDojo._id, userId);
      await loadMyDojo();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to kick member');
    }
  };

  const handleChangeDojoRole = async (userId, newRole) => {
    try {
      await backendService.updateDojoMemberRole(myDojo._id, userId, newRole);
      await loadMyDojo();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to update role');
    }
  };

  const handleApproveDojoRequest = async (requestId) => {
    try {
      await backendService.approveDojoJoinRequest(myDojo._id, requestId);
      await loadMyDojo();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to approve request');
    }
  };

  const handleRejectDojoRequest = async (requestId) => {
    try {
      await backendService.rejectDojoJoinRequest(myDojo._id, requestId);
      await loadMyDojo();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to reject request');
    }
  };

  const handleAcceptDojoInvite = async (invite) => {
    const completion = captureAccount();
    if (!captureEmpty().current()) return;
    try {
      setIsLoading(true);
      await openJoinConfirmation({ ...(invite.dojo || {}), _id: invite.dojo_id }, 'invite');
    } catch (error) {
      if (!completion.current()) return;
      alert(error.response?.data?.error || 'Failed to check dojo requirements');
    } finally {
      if (completion.current()) setIsLoading(false);
    }
  };

  const handleDeclineDojoInvite = async (inviteId) => {
    try {
      await backendService.declineDojoInvite(inviteId);
      await loadDojoInvites();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to decline invite');
    }
  };



  // The compliance warning and gear share the settings entry point.
  const handleOpenRequirementsModal = () => {
    const account = captureAccount();
    const refresh = () => {
      if (!account.current()) return;
      loadMyDojo(); onDojoChange?.();
    };
    openModal(DojoSettingsModal, { backendService, onDojoChange: refresh,
      onRequirementsUpdated: refresh });
  };

  // Dojo chat handlers


  const handleSendDojoMessage = async (e) => {
    e.preventDefault();
    if (!newDojoMessage.trim() || !myDojo?._id) return;
    
    try {
      setIsSendingDojoMessage(true);
      await backendService.sendDojoMessage(myDojo._id, newDojoMessage.trim());
      // Message will be added via WebSocket listener
      setNewDojoMessage('');
      // Scroll to bottom
      setTimeout(() => {
        dojoMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to send message');
    } finally {
      setIsSendingDojoMessage(false);
    }
  };

  const handleCancelDojoInvite = async (inviteId) => {
    try {
      await backendService.cancelDojoInvite(inviteId);
      await loadDojoInvites();
      alert('Invite cancelled.');
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to cancel invite');
    }
  };

  const handleInviteFriendToDojo = async (friend) => {
    const rawId = friend?._id || friend?.user_id || friend?.id || friend;
    const targetUserId = typeof rawId === 'string'
      ? rawId
      : rawId?.$oid || (typeof rawId?.toHexString === 'function' ? rawId.toHexString() : null);

    if (!/^[a-f\d]{24}$/i.test(targetUserId || '')) {
      alert('Could not identify this friend. Please refresh and try again.');
      return;
    }

    try {
      const inviteResult = await backendService.inviteUserToDojo(myDojo._id, targetUserId);
      // Update the row immediately from the successful mutation response;
      // re-fetch as well so the sender-scoped invite record is canonical.
      if (inviteResult?.invite_id) {
        setSentDojoInvites(previous => [
          ...previous.filter(invite => String(invite.user_id) !== String(targetUserId)),
          { _id: inviteResult.invite_id, user_id: targetUserId, status: 'pending' }
        ]);
      }
      await loadDojoInvites();
      const currentDojoName = inviteResult?.target_current_dojo_name;
      alert(
        currentDojoName
          ? `Invite sent! This user is currently in ${currentDojoName}; they can choose to switch.`
          : 'Invite sent!'
      );
    } catch (error) {
      const code = error.response?.data?.code;
      if (code === 'pending_invite') {
        alert('Invite already sent to this user. They need to accept it from their Social panel.');
      } else if (code === 'already_in_dojo') {
        alert('This user is already a member of this dojo.');
      } else {
        alert(error.response?.data?.error || 'Failed to send invite');
      }
    }
  };

  const handleGetInviteLink = async () => {
    // Open the invite choices immediately. A link-generation failure must not
    // block inviting an existing friend or entering an email address.
    setShowInvitePanel(true);
    setInviteLinkError('');
    try {
      const result = await backendService.getDojoInviteLink();
      setInviteLink(result.url);
    } catch (error) {
      setInviteLinkError(error.response?.data?.error || 'Shareable link unavailable');
    }
  };

  const handleCopyInviteLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setInviteCopied(true);
    setTimeout(() => setInviteCopied(false), 2000);
  };

  const handleSendEmailInvite = async () => {
    if (!inviteEmail.trim()) return;
    try {
      setIsLoading(true);
      await backendService.sendDojoEmailInvite(inviteEmail.trim());
      alert('Invite email sent!');
      setInviteEmail('');
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to send email invite');
    } finally {
      setIsLoading(false);
    }
  };

  // Send friend invite by email (invite someone who isn't on the app yet)
  const handleSendFriendInvite = async (email = friendInviteEmail) => {
    if (!email?.trim()) return;
    try {
      setIsSendingFriendInvite(true);
      await backendService.sendAppInvite(email.trim(), false);
      alert(`Invite sent to ${email}!`);
      setFriendInviteEmail('');
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to send invite');
    } finally {
      setIsSendingFriendInvite(false);
    }
  };

  // Pick contacts from phone (Contact Picker API - mobile browsers only)
  const handlePickContacts = async () => {
    // Check if Contact Picker API is available
    if (!('contacts' in navigator && 'ContactsManager' in window)) {
      alert('Contact picker is not available on this device. Please enter an email address manually.');
      return;
    }
    
    try {
      const props = ['email'];
      const opts = { multiple: true };
      const contacts = await navigator.contacts.select(props, opts);
      
      if (contacts.length === 0) return;
      
      // Get emails from selected contacts
      const emails = contacts
        .flatMap(c => c.email || [])
        .filter(e => e && e.trim());
      
      if (emails.length === 0) {
        alert('No email addresses found in selected contacts.');
        return;
      }
      
      // Send invite to each email
      setIsSendingFriendInvite(true);
      let successCount = 0;
      let failCount = 0;
      
      for (const email of emails) {
        try {
          await backendService.sendAppInvite(email.trim(), false);
          successCount++;
        } catch (e) {
          failCount++;
        }
      }
      
      if (successCount > 0) {
        alert(`Sent ${successCount} invite${successCount > 1 ? 's' : ''}!${failCount > 0 ? ` (${failCount} failed)` : ''}`);
      } else {
        alert('Failed to send invites. Some emails may already be registered.');
      }
    } catch (error) {
      // User cancelled or API error
      if (error.name !== 'NotAllowedError') {
        console.error('Contact picker error:', error);
      }
    } finally {
      setIsSendingFriendInvite(false);
    }
  };

  // Check if Contact Picker API is supported
  const isContactPickerSupported = 'contacts' in navigator && 'ContactsManager' in window;

  const handleViewProfile = (user) => {
    if (user?._id) {
      openModal(SocialProfileModal, {
        userId: user._id,
        userName: user.display_name || user.email?.split('@')[0],
        backendService
      });
    }
  };

  const renderAvatar = (user, size = 40, clickable = true, showOnlineStatus = false) => {
    const avatarContent = user?.avatar_url ? (
      <img src={getAvatarVariantUrl(user, avatarVariantForPixelSize(size))} alt="" className="social-avatar" style={{ width: size, height: size }} />
    ) : (
      <div className="social-avatar-placeholder" style={{ width: size, height: size }}>
        {user?.display_name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || '?'}
      </div>
    );

    // Determine status: working-out > online > offline
    const getStatusClass = () => {
      if (user?.workingOut) return 'working-out';
      if (user?.online) return 'online';
      return 'offline';
    };
    
    const getStatusTitle = () => {
      if (user?.workingOut) return `🏋️ ${user.workingOut}`;
      if (user?.online) return 'Online';
      return 'Offline';
    };

    const onlineIndicator = showOnlineStatus && (
      <span 
        className={`avatar-online-indicator ${getStatusClass()}`}
        title={getStatusTitle()}
      ></span>
    );

    if (clickable && user?._id) {
      return (
        <div 
          className="social-avatar-clickable" 
          onClick={(e) => { e.stopPropagation(); handleViewProfile(user); }}
          title="View profile"
        >
          {avatarContent}
          {onlineIndicator}
        </div>
      );
    }
    return (
      <div className="social-avatar-wrapper">
        {avatarContent}
        {onlineIndicator}
      </div>
    );
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  const renderMessages = () => {
    const selectedFriend = friends.find(f => f._id === selectedConversation) ||
      conversations.find(c => c.friend._id === selectedConversation)?.friend;

    // If dojo chat is selected, show dojo chat
    if (showDojoChat && myDojo) {
      return (
        <div className="chat-fullscreen">
          <div className="chat-header">
            <button className="chat-back-btn" onClick={() => setShowDojoChat(false)}>
              ←
            </button>
            <span className="dojo-chat-emoji">{myDojo.emoji || '🥋'}</span>
            <span className="chat-name">{myDojo.name}</span>
            <span className="dojo-chat-member-count">({myDojo.member_count || dojoMembers.length} members)</span>
          </div>
          <div className="chat-messages">
            {dojoMessages.length === 0 ? (
              <div className="no-messages">
                <p>No messages yet. Say hello to your dojo! 🥋</p>
              </div>
            ) : (
              dojoMessages.map((msg, idx) => {
                const sender = msg.sender || msg.from_user;
                const isMe = sender?._id === currentUserId || msg.from_user_id?.toString() === currentUserId;
                return (
                  <div
                    key={msg._id || idx}
                    className={`message ${isMe ? 'sent' : 'received'}`}
                  >
                    {!isMe && (
                      <div className="dojo-message-sender">
                        {sender?.display_name || sender?.email || 'Unknown'}
                      </div>
                    )}
                    <div className="message-content">{msg.content}</div>
                    <div className="message-time">{formatTime(msg.created_at)}</div>
                  </div>
                );
              })
            )}
            <div ref={dojoMessagesEndRef} />
          </div>
          <form className="chat-input-form" onSubmit={handleSendDojoMessage}>
            <input
              type="text"
              className="chat-input"
              placeholder="Message your dojo..."
              value={newDojoMessage}
              onChange={(e) => setNewDojoMessage(e.target.value)}
              maxLength={2000}
            />
            <button 
              type="submit" 
              className="send-btn"
              disabled={!newDojoMessage.trim() || isSendingDojoMessage}
            >
              {isSendingDojoMessage ? '...' : '➤'}
            </button>
          </form>
        </div>
      );
    }

    // If a conversation is selected, show full-screen chat
    if (selectedConversation && selectedFriend) {
      return (
        <div className="chat-fullscreen">
          <div className="chat-header">
            <button className="chat-back-btn" onClick={() => setSelectedConversation(null)}>
              ←
            </button>
            {renderAvatar(selectedFriend, 32, true, true)}
            <span className="chat-name">{selectedFriend?.display_name || selectedFriend?.email}</span>
          </div>
          <div className="chat-messages">
            {messages.length === 0 ? (
              <div className="no-messages">
                <p>No messages yet. Say hello! 👋</p>
              </div>
            ) : (
              messages.map((msg, idx) => (
                <div
                  key={msg._id || idx}
                  className={`message ${msg.from_user_id === selectedConversation ? 'received' : 'sent'}`}
                >
                  <div className="message-content">{msg.content}</div>
                  <div className="message-time">{formatTime(msg.created_at)}</div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>
          <form className="chat-input-form" onSubmit={handleSendMessage}>
            <input
              ref={messageInputRef}
              type="text"
              className="chat-input"
              placeholder="Type a message..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              maxLength={2000}
            />
            <button 
              type="submit" 
              className="send-btn"
              disabled={!newMessage.trim() || isSendingMessage}
            >
              {isSendingMessage ? '...' : '➤'}
            </button>
          </form>
        </div>
      );
    }

    // Show conversation list
    return (
      <div className="conversations-fullscreen">
        <div className="conversations-header">
          <h4>Messages</h4>
          {friends.length > 0 && (
            <button 
              className="new-chat-btn"
              onClick={() => {
                // Show friends to start a new conversation with
                const friendsWithoutConvo = friends.filter(f => !conversations.some(c => c.friend._id === f._id));
                if (friendsWithoutConvo.length === 0) {
                  alert('You already have conversations with all your friends!');
                } else {
                  // Select the first friend without a conversation
                  setSelectedConversation(friendsWithoutConvo[0]._id);
                }
              }}
            >
              + New
            </button>
          )}
        </div>
        
        {conversations.length === 0 && friends.length === 0 && !myDojo ? (
          <div className="no-conversations">
            <p>No conversations yet</p>
            <p className="no-conversations-hint">Add friends or join a dojo to start chatting!</p>
            <button className="social-btn primary" onClick={() => setActiveTab('search')}>
              Find Friends
            </button>
          </div>
        ) : (
          <div className="conversations-list-full">
            {/* Dojo chat at the top if user has a dojo */}
            {myDojo && (
              <div
                className="conversation-item dojo-conversation"
                onClick={() => {
                  setSelectedConversation(null);
                  setShowDojoChat(true);
                }}
              >
                <div className="dojo-conversation-avatar">
                  <span className="dojo-emoji-large">{myDojo.emoji || '🥋'}</span>
                </div>
                <div className="conversation-info">
                  <div className="conversation-name">
                    {myDojo.name}
                    <span className="dojo-badge">Dojo</span>
                  </div>
                  <div className="conversation-preview">
                    {dojoMessages.length > 0 
                      ? `${(dojoMessages[dojoMessages.length - 1].sender || dojoMessages[dojoMessages.length - 1].from_user)?.display_name || 'Someone'}: ${dojoMessages[dojoMessages.length - 1].content?.substring(0, 30)}${dojoMessages[dojoMessages.length - 1].content?.length > 30 ? '...' : ''}`
                      : 'Tap to chat with your dojo'
                    }
                  </div>
                </div>
                {dojoMessages.length > 0 && (
                  <div className="conversation-time">{formatTime(dojoMessages[dojoMessages.length - 1].created_at)}</div>
                )}
              </div>
            )}
            {conversations.map(conv => (
              <div
                key={conv.friend._id}
                className="conversation-item"
                onClick={() => setSelectedConversation(conv.friend._id)}
              >
                {renderAvatar(conv.friend, 44, true, true)}
                <div className="conversation-info">
                  <div className="conversation-name">
                    {renderNameWithYouIndicator(conv.friend.display_name || conv.friend.email, conv.friend._id)}
                    {conv.unread_count > 0 && (
                      <span className="unread-badge">{conv.unread_count}</span>
                    )}
                  </div>
                  <div className="conversation-preview">
                    {conv.last_message.is_from_me && <span className="you-prefix">You: </span>}
                    {conv.last_message.content.substring(0, 40)}
                    {conv.last_message.content.length > 40 && '...'}
                  </div>
                </div>
                <div className="conversation-time">{formatTime(conv.last_message.created_at)}</div>
              </div>
            ))}
            {/* Show friends without conversations */}
            {friends
              .filter(f => !conversations.some(c => c.friend._id === f._id))
              .map(friend => (
                <div
                  key={friend._id}
                  className="conversation-item new-conversation"
                  onClick={() => setSelectedConversation(friend._id)}
                >
                  {renderAvatar(friend, 44, true, true)}
                  <div className="conversation-info">
                    <div className="conversation-name">{renderNameWithYouIndicator(friend.display_name || friend.email, friend._id)}</div>
                    <div className="conversation-preview new-chat">Tap to start chatting</div>
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    );
  };

  const renderFriends = () => (
    <div className="friends-list-container">
      {friends.length === 0 ? (
        <div className="no-friends">
          <p>No friends yet</p>
          <button className="social-btn primary" onClick={() => setActiveTab('search')}>
            Find Friends
          </button>
        </div>
      ) : (
        <div className="friends-grid">
          {friends.map(friend => (
            <div key={friend._id} className="friend-card">
              {renderAvatar(friend, 50, true, true)}
              <div className="friend-card-info">
                <span className="friend-card-name">{renderNameWithYouIndicator(friend.display_name || friend.email, friend._id)}</span>
                {friend.display_name && <span className="friend-card-email">{friend.email}</span>}
              </div>
              <div className="friend-card-actions">
                <button 
                  className="social-btn small primary" 
                  onClick={() => handleStartConversation(friend._id)}
                >
                  Message
                </button>
                <button 
                  className="social-btn small danger" 
                  onClick={() => handleRemoveFriend(friend._id)}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderRequests = () => (
    <div className="requests-container">
      <div className="requests-section">
        <h4>Pending Requests ({pendingRequests.length})</h4>
        {pendingRequests.length === 0 ? (
          <p className="requests-empty">No pending requests</p>
        ) : (
          pendingRequests.map(request => (
            <div key={request._id} className="request-item">
              {renderAvatar(request.from_user)}
              <div className="request-info">
                <span className="request-name">{request.from_user.display_name || 'Unknown'}</span>
              </div>
              <div className="request-actions">
                <button className="social-btn small primary" onClick={() => handleAcceptRequest(request._id)}>
                  Accept
                </button>
                <button className="social-btn small" onClick={() => handleDeclineRequest(request._id)}>
                  Decline
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="requests-section">
        <h4>Sent Requests ({sentRequests.length})</h4>
        {sentRequests.length === 0 ? (
          <p className="requests-empty">No sent requests</p>
        ) : (
          sentRequests.map(request => (
            <div key={request._id} className="request-item">
              {renderAvatar(request.to_user)}
              <div className="request-info">
                <span className="request-name">{request.to_user.display_name || 'Unknown'}</span>
              </div>
              <div className="request-actions">
                <button className="social-btn small" onClick={() => handleCancelRequest(request._id)}>
                  Cancel
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  const renderSearch = () => {
    const hasGymFilter = selectedGymFilter && selectedGymFilter !== 'none';
    const hasTextQuery = searchQuery.length >= 2;
    const showResults = hasGymFilter || hasTextQuery;
    
    return (
      <div className="search-container">
        {/* Gym filter dropdown */}
        {userGyms.length > 0 && (
          <div className="gym-filter-section">
            <label className="gym-filter-label">🏋️ Filter by gym:</label>
            <select
              className="gym-filter-select"
              value={selectedGymFilter}
              onChange={(e) => setSelectedGymFilter(e.target.value)}
            >
              <option value="none">No gym filter</option>
              <option value="all">All my gyms</option>
              {userGyms.map(gym => (
                <option key={gym._id} value={gym._id}>
                  {gym.name}
                </option>
              ))}
            </select>
          </div>
        )}
        
        <input
          type="text"
          className="search-input"
          placeholder={hasGymFilter ? "Filter by name (optional)..." : "Search by name or email..."}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          autoFocus
        />
        
        {isLoading ? (
          <div className="search-loading">Searching...</div>
        ) : !showResults ? (
          <div className="search-empty">
            {userGyms.length > 0 
              ? 'Select a gym above or enter at least 2 characters to search'
              : 'Enter at least 2 characters to search'
            }
          </div>
        ) : searchResults.length === 0 ? (
          <div className="search-empty">
            {hasGymFilter ? 'No users found at this gym' : 'No users found'}
          </div>
        ) : (
          <div className="search-results">
            {searchResults.map(user => (
              <div key={user._id} className="search-result-item">
                {renderAvatar(user)}
                <div className="search-result-info">
                  <span className="search-result-name">{renderNameWithYouIndicator(user.display_name || user.email, user._id)}</span>
                  {user.display_name && <span className="search-result-email">{user.email}</span>}
                  {hasGymFilter && user.gym_stats && (
                    <span className="gym-stats-info">
                      {user.gym_stats.workout_count} workout{user.gym_stats.workout_count !== 1 ? 's' : ''}
                      {user.gym_stats.gyms?.length > 0 && (
                        <span className="gym-name-badge">@ {user.gym_stats.gyms[0].name}</span>
                      )}
                    </span>
                  )}
                </div>
                <div className="search-result-actions">
                  {user.friendship_status?.status === 'accepted' ? (
                    <span className="friendship-status friends">✓ Friends</span>
                  ) : user.friendship_status?.status === 'pending' ? (
                    user.friendship_status.is_sender ? (
                      <button
                        className="social-btn small"
                        onClick={() => handleCancelRequest(user.friendship_status.request_id)}
                      >
                        Cancel Request
                      </button>
                    ) : (
                      <button
                        className="social-btn small primary"
                        onClick={() => handleAcceptRequest(user.friendship_status.request_id)}
                      >
                        Accept
                      </button>
                    )
                  ) : (
                    <button
                      className="social-btn small primary"
                      onClick={() => handleSendRequest(user._id)}
                      disabled={isSendingRequest === user._id}
                    >
                      {isSendingRequest === user._id ? 'Sending...' : 'Add Friend'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Invite friend by email section */}
        <div className="friend-invite-section">
          <div className="friend-invite-header">
            <span className="friend-invite-title">👋 Invite a friend to {backendService.appName || 'the app'}</span>
          </div>
          <div className="friend-invite-row">
            <input
              type="email"
              className="friend-invite-input"
              placeholder="Enter email address..."
              value={friendInviteEmail}
              onChange={(e) => setFriendInviteEmail(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendFriendInvite()}
            />
            <button
              className="social-btn small primary"
              onClick={() => handleSendFriendInvite()}
              disabled={!friendInviteEmail.trim() || isSendingFriendInvite}
            >
              {isSendingFriendInvite ? '...' : '✉️ Send'}
            </button>
          </div>
          {isContactPickerSupported && (
            <button
              className="social-btn contact-picker-btn"
              onClick={handlePickContacts}
              disabled={isSendingFriendInvite}
            >
              📱 Choose from Contacts
            </button>
          )}
        </div>
      </div>
    );
  };

  const totalPendingCount = pendingRequests.length;
  const isDojoChief = myDojo?.user_membership?.role === 'chief';
  const isDojoInstructor = myDojo?.user_membership?.role === 'instructor';
  const canManageDojo = isDojoChief || isDojoInstructor;

  const renderDojo = () => {
    if (membershipStatus === 'pending') return <p role="status">Loading Dojo membership…</p>;
    if (membershipStatus === 'error') return <div role="alert"><p>Unable to load Dojo membership.</p><button type="button" onClick={loadMyDojo}>Retry</button></div>;
    // Find dojo view
    if (dojoView === 'find' && !myDojo) {
      return (
        <div className="dojo-find-container">
          <div className="dojo-find-header">
            <button className="social-btn small" onClick={() => setDojoView('my-dojo')}>← Back</button>
            <h4>Find a Dojo</h4>
          </div>
          <div className="dojo-search-bar">
            <input
              type="text"
              placeholder="Search dojos..."
              value={dojoSearchQuery}
              onChange={(e) => setDojoSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleDojoSearch()}
            />
            <button className="social-btn primary" onClick={handleDojoSearch} disabled={isLoading}>
              Search
            </button>
          </div>
          <div className="dojo-results">
            <DojoCoachMembershipNotice />
            {isLoading ? (
              <div className="dojo-loading">Searching...</div>
            ) : searchDojos.length === 0 ? (
              <div className="dojo-empty">No dojos found</div>
            ) : (
              searchDojos.map(dojo => (
                <div key={dojo._id} className="dojo-result-item">
                  <div className="dojo-result-emoji">{dojo.image_url ? <img src={dojo.image_url} alt="" /> : '🥋'}</div>
                  <div className="dojo-result-info">
                    <span className="dojo-result-name">{dojo.name}</span>
                    <span className="dojo-result-desc">{dojo.description || 'No description'}</span>
                    <span className="dojo-result-meta">{dojo.member_count} members • Chief: {dojo.chief?.display_name || 'Unknown'}</span>
                    {dojo.requirements && (
                      <span className="dojo-result-requirements">📋 Has membership requirements</span>
                    )}
                  </div>
                  <div className="dojo-result-actions">
                    {dojo.has_pending_request ? (
                      <span className="dojo-pending-badge">Pending</span>
                    ) : (
                      <button 
                        className="social-btn small primary"
                        onClick={() => handleJoinDojo(dojo, dojo.visibility)}
                        disabled={isLoading}
                      >
                        {dojo.visibility === 'invite_only' ? 'Request' : 'Join'}
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      );
    }

    // Create dojo view
    if (dojoView === 'create' && !myDojo) {
      return (
        <div className="dojo-create-container">
          <div className="dojo-create-header">
            <button className="social-btn small" onClick={() => setDojoView('my-dojo')}>← Back</button>
            <h4>Create a Dojo</h4>
          </div>
          <form className="dojo-create-form" onSubmit={handleCreateDojo}>
            <div className="dojo-form-group">
              <label>Dojo Name *</label>
              <input type="text" value={newDojoName} onChange={(e) => setNewDojoName(e.target.value)} placeholder="e.g., Iron Warriors" required />
            </div>
            <div className="dojo-form-group">
              <label>Description</label>
              <textarea value={newDojoDescription} onChange={(e) => setNewDojoDescription(e.target.value)} placeholder="What's your dojo about?" />
            </div>
            <div className="dojo-form-group">
              <label>Visibility</label>
              <select value={newDojoVisibility} onChange={(e) => setNewDojoVisibility(e.target.value)}>
                <option value="public">🌐 Public</option>
                <option value="friends_only">👥 Friends Only</option>
                <option value="invite_only">🔒 Invite Only</option>
              </select>
            </div>
            <div className="dojo-form-group">
              <label>Emoji</label>
              <input type="text" value={newDojoEmoji} onChange={(e) => setNewDojoEmoji(e.target.value)} maxLength={2} style={{ width: '60px' }} />
            </div>
            <div className="dojo-form-row">
              <div className="dojo-form-group">
                <label>AI Tone</label>
                <select value={newDojoTone} onChange={(e) => setNewDojoTone(e.target.value)}>
                  {tonePresets.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
                {newDojoTone === 'custom' && (
                  <input
                    type="text"
                    value={newDojoToneCustom}
                    onChange={(e) => setNewDojoToneCustom(e.target.value)}
                    placeholder="Describe your custom AI tone..."
                    className="custom-input"
                    style={{ marginTop: '8px' }}
                  />
                )}
              </div>
              <div className="dojo-form-group">
                <label>Intensity</label>
                <select value={newDojoIntensity} onChange={(e) => setNewDojoIntensity(e.target.value)}>
                  {intensityLevels.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                </select>
                {newDojoIntensity === 'custom' && (
                  <input
                    type="text"
                    value={newDojoIntensityCustom}
                    onChange={(e) => setNewDojoIntensityCustom(e.target.value)}
                    placeholder="Describe your custom intensity..."
                    className="custom-input"
                    style={{ marginTop: '8px' }}
                  />
                )}
              </div>
            </div>
            <div className="dojo-form-actions">
              <button type="submit" className="social-btn primary" disabled={isLoading}>Create Dojo</button>
            </div>
          </form>
        </div>
      );
    }

    // No dojo state
    if (!myDojo) {
      return (
        <div className="dojo-empty-state">
          {dojoInvites.length > 0 && (
            <div className="dojo-invites-section">
              <h4>Dojo Invites ({dojoInvites.length})</h4>
              {dojoInvites.map(invite => (
                <div key={invite._id} className="dojo-invite-item">
                  <div className="dojo-invite-info">
                    <span className="dojo-invite-name">{invite.dojo?.name}</span>
                    <span className="dojo-invite-from">from {invite.inviter?.display_name || 'Unknown'}</span>
                  </div>
                  <div className="dojo-invite-actions">
                    <button className="social-btn small primary" onClick={() => handleAcceptDojoInvite(invite)} disabled={isLoading}>Accept</button>
                    <button className="social-btn small" onClick={() => handleDeclineDojoInvite(invite._id)}>Decline</button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="dojo-no-dojo">
            <span className="dojo-icon">🥋</span>
            <h3>You're not in a Dojo</h3>
            <p>Join a dojo to train with others using shared AI coach settings!</p>
            <div className="dojo-no-dojo-actions">
              <button className="social-btn primary" onClick={() => setDojoView('create')}>Create a Dojo</button>
              <button className="social-btn" onClick={() => setDojoView('find')}>Find a Dojo</button>
            </div>
          </div>
        </div>
      );
    }

    // My dojo view. Settings are opened as a separate modal so this view
    // remains a coherent, editable dojo surface.
    return (
      <div className="dojo-my-container">
        <div className="dojo-header">
          <div className="dojo-header-emoji">{myDojo.ai_coach_settings?.emoji || '🥋'}</div>
          <div className="dojo-header-info">
            <h3>{myDojo.name}</h3>
            <p>{myDojo.description || 'No description'}</p>
            <div className="dojo-header-meta">
              <span className="dojo-visibility-badge">
                {myDojo.visibility === 'public' && '🌐 Public'}
                {myDojo.visibility === 'friends_only' && '👥 Friends Only'}
                {myDojo.visibility === 'invite_only' && '🔒 Invite Only'}
              </span>
              <span className="dojo-member-count">{myDojo.member_count} members</span>
              <span className="dojo-role-badge">
                {myDojo.user_membership?.role === 'chief' && '👑 Chief'}
                {myDojo.user_membership?.role === 'instructor' && '🎓 Instructor'}
                {myDojo.user_membership?.role === 'member' && '🥋 Member'}
              </span>
              <button type="button" className="dojo-settings-gear" aria-label={isDojoChief ? 'Edit dojo settings' : 'View dojo settings'} title={isDojoChief ? 'Edit dojo settings' : 'View dojo settings'} onClick={handleOpenRequirementsModal}>⚙️</button>
              {/* Compliance warning for non-chiefs */}
              {myDojo.user_membership?.role !== 'chief' && myCompliance && !myCompliance.compliant && (
                <div 
                  className="dojo-compliance-warning clickable"
                  onClick={handleOpenRequirementsModal}
                  title="Click to view requirements"
                >
                  ⚠️ Not meeting requirements - {myCompliance.autoRemovalEligible === true ? <>
                    <GracePeriodCountdown
                      firstViolationAt={myCompliance.firstViolationAt}
                      gracePeriodDays={dojoRequirements?.gracePeriodDays || 7}
                      expiredText="expired! Fix now to stay"
                    /> to comply or be removed
                  </> : 'review your settings (advisory; no automatic removal)'}
                </div>
              )}
            </div>
          </div>
        </div>


        <div className="dojo-chat-section">
          <button 
            className="social-btn primary dojo-chat-btn"
            onClick={() => {
              setSelectedConversation(null);
              if (!dojoOnly) setActiveTab('messages');
              setShowDojoChat(true);
            }}
          >
            💬 Open Dojo Chat
            {dojoMessages.length > 0 && (
              <span className="dojo-chat-preview">
                Last: {(dojoMessages[dojoMessages.length - 1].sender || dojoMessages[dojoMessages.length - 1].from_user)?.display_name || 'Someone'}: {dojoMessages[dojoMessages.length - 1].content?.substring(0, 20)}...
              </span>
            )}
          </button>
        </div>

        {canManageDojo && dojoJoinRequests.length > 0 && (
          <div className="dojo-requests-section">
            <h4>Join Requests ({dojoJoinRequests.length})</h4>
            {dojoJoinRequests.map(request => (
              <div key={request._id} className="dojo-request-item">
                <span>{renderNameWithYouIndicator(request.user?.display_name || request.user?.email, request.user?._id)}</span>
                <div className="dojo-request-actions">
                  <button className="social-btn small primary" onClick={() => handleApproveDojoRequest(request._id)}>✓</button>
                  <button className="social-btn small danger" onClick={() => handleRejectDojoRequest(request._id)}>✕</button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="dojo-members-section">
          <div className="dojo-members-header">
            <h4>Members ({dojoMembers.length})</h4>
            {canManageDojo && (
              <button className="social-btn small" onClick={handleGetInviteLink}>
                ➕ Invite
              </button>
            )}
          </div>
          
          {showInvitePanel && (
            <div className="invite-panel">
              <div className="invite-panel-header">
                <h5>Invite to {myDojo?.name}</h5>
                <button className="close-btn" onClick={() => setShowInvitePanel(false)}>×</button>
              </div>
              
              <div className="invite-section">
                <label>Share invite link</label>
                <div className="invite-link-row">
                  {inviteLink ? (
                    <>
                      <input type="text" value={inviteLink} readOnly className="invite-link-input" />
                      <button className="social-btn small" onClick={handleCopyInviteLink}>
                        {inviteCopied ? '✓ Copied!' : '📋 Copy'}
                      </button>
                    </>
                  ) : (
                    <span className="invite-link-status">
                      {inviteLinkError || 'Generating link…'}
                    </span>
                  )}
                </div>
              </div>
              
              <div className="invite-section">
                <label>Send email invite</label>
                <div className="invite-email-row">
                  <input 
                    type="email" 
                    value={inviteEmail} 
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="Enter email address"
                    className="invite-email-input"
                  />
                  <button 
                    className="social-btn small" 
                    onClick={handleSendEmailInvite}
                    disabled={!inviteEmail.trim() || isLoading}
                  >
                    ✉️ Send
                  </button>
                </div>
              </div>
              
              <div className="invite-section">
                <label>Invite existing friend</label>
                <div className="invite-friends-list">
                  {friends
                    .filter(f => !dojoMembers.some(m => m.user_id.toString() === f._id))
                    .slice(0, 5)
                    .map(friend => {
                      const pendingInvite = sentDojoInvites.find(invite => String(invite.user_id) === String(friend._id));
                      return (
                        <div key={friend._id} className="invite-friend-item">
                          {renderAvatar(friend, 28, true, true)}
                          <span>{renderNameWithYouIndicator(friend.display_name || friend.email, friend._id)}</span>
                          {pendingInvite ? (
                            <>
                              <span className="dojo-invite-status">Invite sent</span>
                              <button className="social-btn small" onClick={() => handleCancelDojoInvite(pendingInvite._id)}>Cancel</button>
                            </>
                          ) : (
                            <button className="social-btn small" onClick={() => handleInviteFriendToDojo(friend)}>Invite</button>
                          )}
                        </div>
                      );
                    })}
                  {friends.filter(f => !dojoMembers.some(m => m.user_id.toString() === f._id)).length === 0 && (
                    <span className="no-friends-message">All friends are in this dojo</span>
                  )}
                </div>
              </div>
            </div>
          )}
          {isDojoChief && trailMember?.dojo === String(myDojo._id) && trailMember.owner === backendService.positionOwner && trailMember.login === backendService.authenticatedIdentity && <>
            <button type="button" className="social-btn small" onClick={() => setTrailMember(null)}>Close member trail history</button>
            <BackgroundPositionHistory backendService={backendService} memberId={trailMember.id} memberName={trailMember.name} />
          </>}
          <div className="dojo-members-list">
            {dojoMembers.map(member => (
              <div key={member._id} className={`dojo-member-item${isDojoChief ? ' dojo-member-with-trail' : ''}`}>
                {renderAvatar(member.user, 36, true, true)}
                <div className="dojo-member-info">
                  <span className="dojo-member-name">{renderNameWithYouIndicator(member.user?.display_name || member.user?.email, member.user_id)}</span>
                  <span className="dojo-member-role">
                    {member.role === 'chief' && '👑 Chief'}
                    {member.role === 'instructor' && '🎓 Instructor'}
                    {member.role === 'member' && '🥋 Member'}
                  </span>
                </div>
                <div className="dojo-member-actions">
                  {isDojoChief && <button type="button" className="social-btn small background-trail-button" onClick={() => setTrailMember({ id: String(member.user_id), name: member.user?.display_name || 'Selected member', dojo: String(myDojo._id), owner: backendService.positionOwner, login: backendService.authenticatedIdentity })}>Trail history for {member.user?.display_name || 'member'}</button>}
                  {member.user_id?.toString() !== currentUserId && (
                    <button 
                      className="social-btn small primary" 
                      onClick={() => handleStartConversation(member.user_id)}
                      title="Send message"
                    >
                      💬
                    </button>
                  )}
                  {isDojoChief && member.role !== 'chief' && (
                    <>
                      <select value={member.role} onChange={(e) => handleChangeDojoRole(member.user_id, e.target.value)}>
                        <option value="member">Member</option>
                        <option value="instructor">Instructor</option>
                      </select>
                      <button className="social-btn small danger" onClick={() => handleKickMember(member.user_id)}>Remove</button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>


        {!isDojoChief && <div className="dojo-actions">
          <button className="social-btn danger" onClick={handleLeaveDojo}>Leave Dojo</button>
        </div>}
      </div>
    );
  };

  return (
    <div className="social-modal">
      <div className="social-modal-header">
        <h2>{dojoOnly ? 'Dojo' : 'Social'}</h2>
        {!dojoOnly && streams?.available && streams.openMedia && <button type="button" className="social-close-btn" onClick={() => { close?.(); streams.openMedia(); }}>Videos in Media</button>}
      </div>

      {!dojoOnly && <div className="modal-tabs">
        <button
          className={`modal-tab ${activeTab === 'messages' ? 'active' : ''}`}
          onClick={() => setActiveTab('messages')}
          title="Messages"
        >
          💬
        </button>
        <button
          className={`modal-tab ${activeTab === 'friends' ? 'active' : ''}`}
          onClick={() => setActiveTab('friends')}
          title="Friends"
        >
          👥 <span className="tab-count">{friends.length}</span>
        </button>

        <button
          className={`modal-tab ${activeTab === 'requests' ? 'active' : ''}`}
          onClick={() => setActiveTab('requests')}
          title="Friend Requests"
        >
          📬 {totalPendingCount > 0 && <span className="tab-badge">{totalPendingCount}</span>}
        </button>
        <button
          className={`modal-tab ${activeTab === 'search' ? 'active' : ''}`}
          onClick={() => setActiveTab('search')}
          title="Find Friends"
        >
          🔍
        </button>
      </div>}

      <div className="social-modal-body">
        {activeTab === 'messages' && renderMessages()}
        {activeTab === 'friends' && renderFriends()}
        {dojoOnly && (showDojoChat ? renderMessages() : renderDojo())}
        {activeTab === 'requests' && renderRequests()}
        {activeTab === 'search' && renderSearch()}
      </div>

      <div className="social-modal-footer">
        <button className="social-close-btn" onClick={close}>Close</button>
      </div>
      
      {/* Join Dojo Confirmation Modal */}
      {membershipStatus === 'empty' && joinConfirmDojo && (
        <div className="join-confirm-overlay">
          <div className="join-confirm-modal">
            <h3>Join {joinConfirmDojo.name}?</h3>
            <DojoCoachMembershipNotice />
            {joinConfirmDojo.visibility === 'invite_only' && <p>Requesting access does not change your settings. If invited, you must review the current requirements and accept them again before joining.</p>}
            
            {joinEligibility?.requirements && (
              <>
              {joinEligibility.requirements.location_capture_enabled && <p role="alert">This dojo requires fresh device GPS when you finish each activity. If permission is denied, the activity stays pending. Gym tracking and saved gyms do not replace GPS.</p>}
              {joinEligibility.requirements.privacy?.position === 'public' && <p role="alert">Public Position shares your exact coordinates with anyone.</p>}
              <div className="join-requirements-section">
                <h4>📋 Membership Requirements</h4>
                {joinEligibility.requirements.location_capture_enabled && (
                  <p>Capture position when filing: {joinEligibility.currentStats?.location_capture_enabled === true ? 'enabled' : 'disabled'} → enabled</p>
                )}
                {Object.entries(joinEligibility.requirements.privacy || {})
                  .filter(([key, audience]) => key !== 'stats' && audience)
                  .map(([key, audience]) => {
                    const current = joinEligibility.currentStats?.privacy?.[key] ??
                      joinEligibility.violations?.find(v => v.type === 'privacy' && v.activityType === key)?.current;
                    const currentLabel = Array.isArray(current) ? (current.length ? current.join(', ') : 'private') : (current || 'private');
                    return <p key={key}>{key === 'position' ? 'Position' : key}: {currentLabel.replaceAll('dojo_chief', 'Dojo Chief')} → must include {audience === 'dojo_chief' ? 'Dojo Chief' : audience}</p>;
                  })}
                {joinEligibility.requirements.performance?.enabled && (
                  <div className="join-req-category">
                    <span className="join-req-label">📊 Performance Requirements:</span>
                    <ul>
                      {joinEligibility.requirements.performance.consistency?.enabled && (
                        <li>Consistency: {Math.round(joinEligibility.requirements.performance.consistency.minRate * 100)}%</li>
                      )}
                      {joinEligibility.requirements.performance.nutrition?.enabled && (
                        <li>Nutrition: {Math.round(joinEligibility.requirements.performance.nutrition.minRate * 100)}%</li>
                      )}
                      {joinEligibility.requirements.performance.intensity?.enabled && (
                        <li>Intensity: {Math.round(joinEligibility.requirements.performance.intensity.minRate * 100)}%</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
              </>
            )}
            
            {joinEligibility?.suggestedCoachAutoAcceptance && Object.keys(joinEligibility.suggestedCoachAutoAcceptance).length > 0 && (
              <div className="join-req-category">
                <span className="join-req-label">🤖 Coach settings required to join:</span>
                <ul>
                  {Object.keys(joinEligibility.suggestedCoachAutoAcceptance).map(key => (
                    <li key={key}>{key === 'daily_insight_changes' ? 'Daily insight changes' : 'Exercise coach changes'}: automatic acceptance enabled</li>
                  ))}
                </ul>
                {joinConfirmDojo.visibility !== 'invite_only' && <label className="join-adjust-checkbox">
                  <input type="checkbox" checked={joinAcceptCoachSettings} onChange={(e) => setJoinAcceptCoachSettings(e.target.checked)} />
                  <span>I accept enabling these coach settings to join this dojo</span>
                </label>}
              </div>
            )}

            {/* Eligibility Status */}

            {joinEligibility?.eligible ? (
              <div className="join-eligible-status success">
                ✅ You meet all requirements!
              </div>
            ) : (
              <div className="join-eligible-status warning">
                <p>⚠️ You don't currently meet all requirements:</p>
                <ul>
                  {joinEligibility?.violations?.map((v, i) => (
                    <li key={i}>
                      {v.type === 'privacy' && (
                        <>{v.activityType}: your sharing is "{Array.isArray(v.current) ? (v.current.length === 0 ? 'private' : v.current.join(', ')) : v.current}" but must include "{v.required}"</>
                      )}
                      {v.type === 'performance' && (
                        <>{v.metric}: {v.current !== null ? `${Math.round(v.current * 100)}%` : 'no data'} (need {Math.round(v.required * 100)}%)</>
                      )}
                      {v.type === 'body_composition' && (
                        <>{v.metric}: {v.current?.toFixed(1) || 'no data'}</>
                      )}
                    </li>
                  ))}
                </ul>
                
                {joinEligibility?.violations?.some(v => v.type !== 'privacy' && v.type !== 'location_capture') && (
                  <p className="join-warning-note">⚠️ Other requirements must be met before joining.</p>
                )}
              </div>
            )}

            <DojoJoinSettingsConsent eligibility={joinEligibility} accepted={joinAcceptRequiredSettings} onAccept={setJoinAcceptRequiredSettings} backgroundAccepted={joinAcceptBackground} onAcceptBackground={setJoinAcceptBackground}
              requestOnly={joinConfirmDojo.visibility === 'invite_only'} />
            <div className="join-confirm-buttons">
              <button className="social-btn" onClick={handleCancelJoin}>Cancel</button>
              <button
                className="social-btn primary"
                onClick={handleConfirmJoin}
                disabled={isLoading || (joinConfirmDojo.visibility !== 'invite_only' && joinSettingChanges(joinEligibility).needsConsent && !joinAcceptRequiredSettings) ||
                  (joinConfirmDojo.visibility !== 'invite_only' && joinSettingChanges(joinEligibility).needsBackgroundConsent && !joinAcceptBackground) ||
                  (joinConfirmDojo.visibility !== 'invite_only' && joinSettingChanges(joinEligibility).blocking) ||
                  (joinConfirmDojo.visibility !== 'invite_only' && Object.keys(joinEligibility?.suggestedCoachAutoAcceptance || {}).length > 0 && !joinAcceptCoachSettings)}
              >
                {isLoading ? 'Joining...' : joinConfirmDojo.visibility === 'invite_only' ? 'Request to Join' : 'Join Dojo'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SocialModal;
